import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/shared/api/useApi';
import type { PluginInstallation } from '@/shared/api/types/plugins';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@/shared/components';
import { PluginProblem } from './PluginProblem';
import { usePluginDialogFocus } from './usePluginDialogFocus';

export function PluginLifecycleDialog({
  installation,
  action,
  onClose,
}: {
  installation: PluginInstallation;
  action: 'disable' | 'revoke';
  onClose: () => void;
}) {
  const api = useApi();
  const returnFocus = usePluginDialogFocus();
  const cache = useQueryClient();
  const mutation = useMutation({
    networkMode: 'always',
    mutationFn: () => api.plugins.changeState(installation.id, action, installation.grantRevision),
    onSuccess: onClose,
    onSettled: () => cache.invalidateQueries({ queryKey: ['plugins'] }),
  });
  const label = action === 'disable' ? 'Plugin deaktivieren' : 'Zugriff endgültig entziehen';
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !mutation.isPending) onClose();
      }}
    >
      <DialogContent onCloseAutoFocus={returnFocus}>
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
          <DialogDescription>
            {action === 'disable'
              ? 'Der Zugriff aller Teammitglieder endet sofort. Gespeicherte Daten bleiben höchstens 30 Tage gesperrt erhalten. Eine erneute Aktivierung benötigt Ihre ausdrückliche Freigabe.'
              : 'Der Zugriff endet sofort. GuestAI erhält den Auftrag, die Daten dieser Installation zu löschen. Für eine spätere Nutzung ist eine neue Installation erforderlich.'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <PluginProblem error={mutation.error} />
        </DialogBody>
        <DialogFooter className="flex-col items-stretch sm:flex-row sm:items-center">
          <Button type="button" size="lg" onClick={onClose} disabled={mutation.isPending}>
            Abbrechen
          </Button>
          <Button
            type="button"
            size="lg"
            variant="destructive"
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {action === 'disable' ? 'Deaktivieren' : 'Endgültig entziehen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
